import { useState, useEffect, useCallback } from 'react';
import { apiJson } from '../../lib/api';
import { OrgManager } from './OrgManager';
import { UserManager } from './UserManager';
import { AuditLog } from './AuditLog';
import { AccessControlWorkbench } from '../access/AccessControlWorkbench';
import { QuarantineWorkbench } from '../quarantine/QuarantineWorkbench';
import { DriftWorkbench } from '../drift/DriftWorkbench';
import { ProvenanceWorkbench } from '../provenance/ProvenanceWorkbench';
import { CostLedgerWorkbench } from '../cost/CostLedgerWorkbench';

// ── Types ────────────────────────────────────────────────────

type Section =
  | 'overview'
  | 'orgs'
  | 'users'
  | 'access'
  | 'quarantine'
  | 'drift'
  | 'provenance'
  | 'cost'
  | 'audit';

interface NavItem {
  id: Section;
  label: string;
  icon: string; // SVG path
}

interface AuditEntry {
  ts: string;
  actor_github_id: string;
  action: string;
  org_id: string | null;
  target_id: string | null;
  detail: Record<string, unknown>;
}

// ── Nav definitions ──────────────────────────────────────────

const NAV_ITEMS: NavItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  },
  {
    id: 'orgs',
    label: 'Organizations',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  },
  {
    id: 'users',
    label: 'Users',
    icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z',
  },
  {
    id: 'access',
    label: 'Access Control',
    icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4',
  },
  {
    id: 'quarantine',
    label: 'Quarantine',
    icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-1.333-2.694-1.333-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z',
  },
  {
    id: 'drift',
    label: 'Drift Detection',
    icon: 'M22 12h-4l-3 9L9 3l-3 9H2',
  },
  {
    id: 'provenance',
    label: 'Provenance',
    icon: 'M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22',
  },
  {
    id: 'cost',
    label: 'Cost & Usage',
    icon: 'M12 1v22 M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
  },
  {
    id: 'audit',
    label: 'Audit Trail',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  },
];

// ── Action colors for audit ──────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  'auth.login': '#8be9fd',
  'org.create': '#4f8fff',
  'org.update': '#bd93f9',
  'org.delete': '#ff5555',
  'org.invite': '#bd93f9',
  'org.member.remove': '#ff5555',
  'invitation.accept': '#4f8fff',
  'user.update': '#8be9fd',
};

// ── Component ────────────────────────────────────────────────

export function MissionControlWorkbench() {
  const [section, setSection] = useState<Section>('overview');

  return (
    <div className="mc-root">
      {/* ── Sidebar ─────────────────────────────────────────── */}
      <nav className="mc-sidebar" data-testid="mc-sidebar">
        {/* Title */}
        <div className="mc-sidebar-title">
          <div className="mc-sidebar-title-text">Mission Control</div>
          <div className="mc-sidebar-subtitle">Platform security and governance</div>
        </div>

        {NAV_ITEMS.map((item) => {
          const active = section === item.id;
          return (
            <button
              key={item.id}
              data-testid={`mc-nav-${item.id}`}
              onClick={() => setSection(item.id)}
              className={`mc-nav-item ${active ? 'mc-nav-item--active' : ''}`}
            >
              <svg
                viewBox="0 0 24 24"
                width={16}
                height={16}
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <path d={item.icon} />
              </svg>
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* ── Main content ────────────────────────────────────── */}
      <main className="mc-content" data-testid="mc-content">
        {section === 'overview' && <OverviewDashboard onNavigate={setSection} />}
        {section === 'orgs' && (
          <SectionShell title="Organizations" subtitle="Manage workspaces and team boundaries">
            <OrgManager />
          </SectionShell>
        )}
        {section === 'users' && (
          <SectionShell title="Users" subtitle="Platform identity and access management">
            <UserManager />
          </SectionShell>
        )}
        {section === 'access' && <AccessControlWorkbench />}
        {section === 'quarantine' && <QuarantineWorkbench />}
        {section === 'drift' && <DriftWorkbench />}
        {section === 'provenance' && <ProvenanceWorkbench />}
        {section === 'cost' && <CostLedgerWorkbench />}
        {section === 'audit' && (
          <SectionShell title="Audit Trail" subtitle="Chronological record of platform events">
            <AuditLog />
          </SectionShell>
        )}
      </main>
    </div>
  );
}

// ── Section shell wrapper ────────────────────────────────────

function SectionShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 className="mc-section-title">{title}</h2>
        <p className="mc-section-subtitle">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

// ── Overview Dashboard ───────────────────────────────────────

function OverviewDashboard({ onNavigate }: { onNavigate: (s: Section) => void }) {
  const [userCount, setUserCount] = useState<number | null>(null);
  const [orgCount, setOrgCount] = useState<number | null>(null);
  const [costToday, setCostToday] = useState<string | null>(null);
  const [quarantineCount, setQuarantineCount] = useState<number | null>(null);
  const [recentAudit, setRecentAudit] = useState<AuditEntry[]>([]);

  const fetchMetrics = useCallback(() => {
    apiJson<{ users: unknown[] }>('/api/admin/users')
      .then((d) => setUserCount(d.users?.length ?? 0))
      .catch(() => {});

    apiJson<{ orgs: unknown[] }>('/api/admin/orgs')
      .then((d) => setOrgCount(d.orgs?.length ?? 0))
      .catch(() => {});

    fetch('/api/cost/summary')
      .then((r) => r.json())
      .then((d) => {
        const cost = d.total_cost_usd ?? 0;
        setCostToday(cost < 0.01 ? '$0.00' : `$${cost.toFixed(2)}`);
      })
      .catch(() => {});

    fetch('/api/quarantine')
      .then((r) => r.json())
      .then((d) => setQuarantineCount(d.items?.length ?? 0))
      .catch(() => setQuarantineCount(0));

    apiJson<{ entries: AuditEntry[] }>('/api/admin/audit')
      .then((d) => setRecentAudit((d.entries || []).slice(0, 5)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const metrics: {
    label: string;
    value: string;
    accent: string;
    icon: string;
  }[] = [
    {
      label: 'Total Users',
      value: userCount !== null ? String(userCount) : '--',
      accent: 'var(--ot-accent, #4f8fff)',
      icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 7a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75',
    },
    {
      label: 'Active Orgs',
      value: orgCount !== null ? String(orgCount) : '--',
      accent: 'var(--ot-cyan, #38bdf8)',
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1',
    },
    {
      label: 'RBAC Denials (24h)',
      value: '0',
      accent: 'var(--ot-green, #34d399)',
      icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    },
    {
      label: 'Quarantine Items',
      value: quarantineCount !== null ? String(quarantineCount) : '--',
      accent: 'var(--ot-orange, #ff9f43)',
      icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-1.333-2.694-1.333-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z',
    },
    {
      label: 'Cost (24h)',
      value: costToday ?? '--',
      accent: 'var(--ot-purple, #a78bfa)',
      icon: 'M12 1v22 M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
    },
    {
      label: 'Drift Alerts',
      value: '0',
      accent: 'var(--ot-cyan, #38bdf8)',
      icon: 'M22 12h-4l-3 9L9 3l-3 9H2',
    },
  ];

  const quickActions: { label: string; target: Section }[] = [
    { label: 'Invite User', target: 'users' },
    { label: 'Create Org', target: 'orgs' },
    { label: 'Review Quarantine', target: 'quarantine' },
  ];

  return (
    <div style={{ padding: 24 }} data-testid="mc-overview">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 className="mc-section-title">Mission Control</h2>
        <p className="mc-section-subtitle">Platform security and governance</p>
      </div>

      {/* ── Metric cards 3x2 ──────────────────────────────── */}
      <div className="mc-metrics-grid" data-testid="mc-metrics">
        {metrics.map((m) => (
          <div key={m.label} className="mc-metric-card" data-testid="mc-metric-card">
            {/* top glow line */}
            <div
              className="mc-metric-glow"
              style={{
                background: `linear-gradient(90deg, transparent, ${m.accent}44, transparent)`,
              }}
            />
            <div className="mc-metric-header">
              <svg
                viewBox="0 0 24 24"
                width={14}
                height={14}
                fill="none"
                stroke={m.accent}
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, opacity: 0.7 }}
              >
                <path d={m.icon} />
              </svg>
              <span className="mc-metric-label">{m.label}</span>
            </div>
            <span className="mc-metric-value" style={{ color: m.accent }}>
              {m.value}
            </span>
            <span className="mc-metric-trend">--</span>
          </div>
        ))}
      </div>

      {/* ── Recent audit entries ───────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <h3 className="mc-subsection-title">Recent Activity</h3>
        <div className="mc-audit-panel">
          {recentAudit.length === 0 ? (
            <div className="mc-empty-state">No audit entries yet.</div>
          ) : (
            recentAudit.map((entry, i) => {
              const actionColor = ACTION_COLORS[entry.action] || '#8b949e';
              return (
                <div
                  key={i}
                  className="mc-audit-row"
                  style={{
                    borderBottom:
                      i < recentAudit.length - 1
                        ? '1px solid var(--ot-border, rgba(255,255,255,0.06))'
                        : 'none',
                  }}
                >
                  <span className="mc-audit-ts">
                    {new Date(entry.ts).toLocaleString()}
                  </span>
                  <span className="mc-audit-actor">{entry.actor_github_id}</span>
                  <span>
                    <span
                      className="mc-audit-action-badge"
                      style={{
                        background: `${actionColor}14`,
                        color: actionColor,
                        border: `1px solid ${actionColor}30`,
                      }}
                    >
                      {entry.action}
                    </span>
                  </span>
                  <span className="mc-audit-org">{entry.org_id || '-'}</span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Quick actions ─────────────────────────────────── */}
      <div>
        <h3 className="mc-subsection-title">Quick Actions</h3>
        <div className="mc-quick-actions">
          {quickActions.map((qa) => (
            <button
              key={qa.label}
              onClick={() => onNavigate(qa.target)}
              className="mc-quick-action-btn"
              data-testid={`mc-action-${qa.target}`}
            >
              {qa.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
